import { Text } from '@fozito/common-ui';
import { Popover } from 'antd';
import fozito_logo from 'assets/logo.svg';
import { get } from 'lodash';
import authService from 'modules/auth/services';
import { cashierCtrl } from 'modules/POS/Cashier/cashier.controller';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import storage from 'utils/sessionStorage';
import { configs, HomeMenu, MenuItemConfig, MenuOptions } from '.';
import setting_icon from './icons/setting-icon.svg';
import { alertConfirm } from 'widgets/ModalConfirm';
import ModalQuickScanProduct, { useModalQuickScanProductRef } from 'modules/settings/product/widgets/QuickScanProduct';
import { verifyPasscode } from 'modules/shop/widgets/ModalCheckPassCode';

const menuList = [
  MenuOptions.HOME,
  MenuOptions.CASHIER,
  MenuOptions.SCAN_QR, 
  MenuOptions.TICKETS,
  MenuOptions.CREDIT_CARDS,
  MenuOptions.REPORT, 
  MenuOptions.PRODUCT,
  MenuOptions.PROMOTIONS,
  MenuOptions.REFRESH,
];

const settingMenuList = [
  MenuOptions.STAFF,
  MenuOptions.CUSTOMERS,
  MenuOptions.GIFT_CARD,
  MenuOptions.SETTING,
  MenuOptions.LOGOUT,
];

const VerticalHeader = () => {
  const { shop_id = '' } = useParams<{ shop_id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const path = useLocation().pathname;
  const scanProductRef = useModalQuickScanProductRef();
  const activePath = path.replace(`/store/${shop_id}`, '');

  const activeHome = (path: string) => {
    if (path === '/') return activePath === '' || activePath === '/';
    if (activePath.includes('/setting')) return path === activePath;
    
    return activePath.includes(path);
  };

  const onNav = (path: string) =>
    navigate(path == '/' ? `/store/${shop_id}` : `/store/${shop_id}${path}`);
  const onNavClick = (type: MenuOptions, config: MenuItemConfig) => () => {

    const callback = () => {
      if (config.button) {
        switch (type) {
          case MenuOptions.CASHIER:
            navigate(`/store/${shop_id}`);
            break;
          case MenuOptions.SCAN_QR:
            scanProductRef.current?.open();
            break;
          case MenuOptions.REFRESH:
            navigate(0);
            break;
          case MenuOptions.LOGOUT:
            alertConfirm(() => {
              dispatch(authService.slice.actions.expiredToken());
              storage.token.set('');
              storage.refreshToken.set('');
              navigate('/login');
            }, {
              message: 'Are you about to logout the system?',
            });
            break;
          default:
            break;
        }
        return;
      }
      if (config.private) return verifyPasscode(() => onNav(config.path || ''), 'ADMIN');
      return onNav(config.path || '');
    };

    if (!activePath || activePath === '/') {
      return cashierCtrl.autoSaveTicket(callback);
    }
    callback();
  };
  return (
    <Container>
      <div className="logo">
        <img src={fozito_logo} alt='fozito_logo' style={{ width: 114, height: 'auto' }} />
      </div>
      <Menus>
        {menuList.map((key) => {
          const value = get(configs, key);
          if (!value) return null;
          return <HomeMenu
            active={activeHome(value.path || '')}
            key={key}
            disabled={value.disabled}
            type={key as keyof typeof MenuOptions}
            onClick={onNavClick(key as MenuOptions, value)}
          />;
        })}
      </Menus>
      <SettingGroup onNavClick={onNavClick} activeHome={activeHome} />
      <ModalQuickScanProduct ref={scanProductRef} />
    </Container>
  );
};

export default VerticalHeader;
const FloatingButtonStyled = styled.button`
display: flex;
width: 130px;
height: 89px;
padding: 16px 10px;
flex-direction: column;
justify-content: center;
align-items: center;
gap: 4px;
position: absolute;
left: 6px;
bottom: 24px;
border-radius: 10px;
background: #CBD5E1;

&:hover,
&:active {
  opacity: 0.8;
  transform: scale(1.05);
  transition: all 0.1s;
}

&:disabled {
  opacity: 0.7;
  background: #FFF;
  &:hover,
  &:active {
    opacity: 1;
    cursor: not-allowed;
    transform: none;
    transition: none;
  }
}
`;
const SettingPopoverContainer = styled.div`
display: flex;
padding: 8px;
flex-direction: column;
align-items: flex-start;
align-self: stretch;
border-bottom: 1px solid #E5E6EB;
background: #CBD5E1;
box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.40), 0px 4px 8px -3px rgba(45, 35, 66, 0.30), 0px -3px 0px 0px #D6D6E7 inset;
`;
type SettingGroupProps = {
  activeHome: (path: string) => boolean;
  onNavClick: (type: MenuOptions, config: MenuItemConfig) => () => void
};
const SettingGroup = ({ activeHome, onNavClick }: SettingGroupProps) => {
  const [open, setOpen] = useState(false);
  const content = useMemo(() =>
    <SettingPopoverContainer>
      {settingMenuList.map((key) => {
        const value = get(configs, key);
        if (!value) return null;
        return <HomeMenu
          active={activeHome(value.path || '')}
          key={key}
          disabled={value.disabled}
          type={key as keyof typeof MenuOptions}
          onClick={() => [setOpen(false), onNavClick(key as MenuOptions, value)()]}
        />;
      })}
    </SettingPopoverContainer>, [activeHome, onNavClick]);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="rightBottom"
      styles={{ body: { padding: 0 } }}
      title={''} content={content}
      overlayClassName='side-bar-setting-overlay'
      align={{ offset: [15, 10] }}
    >
      <FloatingButtonStyled type='button'>
        <img src={setting_icon} alt="setting_icon" style={{ width: 38, height: 38 }} />
        <Text content_content_2_16_medium>Setting</Text>
      </FloatingButtonStyled>
    </Popover>

  );
};

const Menus = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  flex: 1;
`;
const Container = styled.div`
  border-bottom: 1px solid #CCD4DC;
  background: #FFF;
  box-shadow: 4px 0px 8px -4px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-right: 2px;
  gap: 24px;

  .logo {
    padding: 16px;
    padding-top: 32px;
    padding-bottom: 24px;
  }
`;